import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { M07FinancialSummary } from '../M07FinancialSummary';
import { M07FeeOverview } from '../M07FeeOverview';
import { M07EstimationTable } from '../M07EstimationTable';
import { M07ProductOverview } from '../M07ProductOverview';
import { fontWeights, borderRadius, spacing } from '../../theme';

interface M07DetailsViewProps {
  basePremium: number;
  sumAssured: string;
  surrender20LowText: string;
  surrender20HighText: string;
  term: string;
  activeTableTab: 'overview' | 'estimation' | 'overview_products';
  setActiveTableTab: (tab: 'overview' | 'estimation' | 'overview_products') => void;
  ridersTotal: number;
  totalPremiumNum: number;
  topUpAmount: number;
  totalTopUp: number;
  cashflowHigh: any[];
  cashflow: any[];
  selectedScenario: 'high' | 'low';
  setSelectedScenario: (sc: 'high' | 'low') => void;
  showFullTable: boolean;
  setShowFullTable: (show: boolean) => void;
  selectedRiders: string[];
  r5Mode: 'attached' | 'main' | 'complimentary';
  r1Package: 'silver' | 'gold' | 'platinum';
  r2LimitCuoi: number;
  r3DailyAllowance: number;
  r4PhysicalLimit: number;
  r5SumAssured: number;
  r5Term: string | number;
  riderPremiumOverrides?: { [riderId: string]: number };
}

export const M07DetailsView: React.FC<M07DetailsViewProps> = ({
  basePremium,
  sumAssured,
  surrender20LowText,
  surrender20HighText,
  term,
  activeTableTab,
  setActiveTableTab,
  ridersTotal,
  totalPremiumNum,
  topUpAmount,
  totalTopUp,
  cashflowHigh,
  cashflow,
  selectedScenario,
  setSelectedScenario,
  showFullTable,
  setShowFullTable,
  selectedRiders,
  r5Mode,
  r1Package,
  r2LimitCuoi,
  r3DailyAllowance,
  r4PhysicalLimit,
  r5SumAssured,
  r5Term,
  riderPremiumOverrides,
}) => {
  return (
    <>
      <M07FinancialSummary
        basePremium={basePremium}
        sumAssured={sumAssured}
        surrender20LowText={surrender20LowText}
        surrender20HighText={surrender20HighText}
        term={term}
      />

      <View style={styles.card}>
        <Text style={styles.cardTitle}>Bảng ước tính chi tiết</Text>

        <View style={styles.tableTabContainer}>
          <TouchableOpacity
            style={[styles.tableTabBtn, activeTableTab === 'overview' && styles.tableTabBtnActive]}
            onPress={() => setActiveTableTab('overview')}
          >
            <Text
              style={[
                styles.tableTabBtnText,
                activeTableTab === 'overview' && styles.tableTabBtnTextActive,
              ]}
            >
              Tổng quan phí
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.tableTabBtn, activeTableTab === 'estimation' && styles.tableTabBtnActive]}
            onPress={() => setActiveTableTab('estimation')}
          >
            <Text
              style={[
                styles.tableTabBtnText,
                activeTableTab === 'estimation' && styles.tableTabBtnTextActive,
              ]}
            >
              Bảng ước tính
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.tableTabBtn,
              activeTableTab === 'overview_products' && styles.tableTabBtnActive,
            ]}
            onPress={() => setActiveTableTab('overview_products')}
          >
            <Text
              style={[
                styles.tableTabBtnText,
                activeTableTab === 'overview_products' && styles.tableTabBtnTextActive,
              ]}
            >
              Tổng quan sản phẩm
            </Text>
          </TouchableOpacity>
        </View>

        {activeTableTab === 'overview' && (
          <M07FeeOverview
            basePremium={basePremium}
            ridersTotal={ridersTotal}
            totalPremiumNum={totalPremiumNum}
            topUpAmount={topUpAmount}
            totalTopUp={totalTopUp}
            cashflowHigh={cashflowHigh}
          />
        )}

        {activeTableTab === 'estimation' && (
          <M07EstimationTable
            cashflow={cashflow}
            basePremium={basePremium}
            selectedScenario={selectedScenario}
            setSelectedScenario={setSelectedScenario}
            showFullTable={showFullTable}
            setShowFullTable={setShowFullTable}
          />
        )}

        {activeTableTab === 'overview_products' && (
          <M07ProductOverview
            sumAssured={sumAssured}
            term={term}
            basePremium={basePremium}
            selectedRiders={selectedRiders}
            r5Mode={r5Mode === 'main' ? 'attached' : r5Mode}
            totalPremiumNum={totalPremiumNum}
            r1Package={r1Package}
            r2LimitCuoi={r2LimitCuoi}
            r3DailyAllowance={r3DailyAllowance}
            r4PhysicalLimit={r4PhysicalLimit}
            r5SumAssured={r5SumAssured}
            r5Term={r5Term as any}
            riderPremiumOverrides={riderPremiumOverrides}
          />
        )}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[4],
    borderWidth: 1,
    borderColor: '#e2e8f0',
    gap: spacing[3],
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: '#1e293b',
  },
  tableTabContainer: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    borderRadius: borderRadius.sm,
    padding: 3,
    gap: 2,
  },
  tableTabBtn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing[2],
    borderRadius: borderRadius.sm - 1,
  },
  tableTabBtnActive: {
    backgroundColor: '#ffffff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  tableTabBtnText: {
    fontSize: 12,
    fontWeight: fontWeights.medium,
    color: '#64748b',
  },
  tableTabBtnTextActive: {
    color: '#00A651',
    fontWeight: fontWeights.bold,
  },
});
